import { FeaturesFilter, HeatingType, LifestyleCategory, PropertySubType, PropertyType } from '@ev-tech/search-modules-api';

import type { SearchModuleTranslationKey } from '../types/typedTranslationKeys';
import { PROPERTY_SUBTYPE_PREFIX, PROPERTY_TYPE_PREFIX } from '../consts/consts';

/**
 * Type guard for dynamically constructed i18n keys.
 * Use the default form for a basic "key exists in this translations object" check.
 * Pass `TKey` when the caller needs to narrow to a specific subset of translation keys.
 */
export const hasTranslationKey = <
  TTranslations extends Record<string, unknown>,
  TKey extends string = Extract<keyof TTranslations, string>,
>(
  translations: TTranslations,
  key: string,
): key is Extract<TKey, keyof TTranslations> => key in translations;

type PropertyTypeTranslationKeySuffix = (typeof PropertyType)[keyof typeof PropertyType];

type PropertyTypeTranslationKey = Extract<
  SearchModuleTranslationKey,
  `search.searchbar.advFilter.propertyType.${PropertyTypeTranslationKeySuffix}`
>;

export const getPropertyTypeKey = (propertyType: string): PropertyTypeTranslationKey | '' => {
  switch (propertyType) {
    case PropertyType.agriculture_and_forestry:
      return `${PROPERTY_TYPE_PREFIX}agriculture_and_forestry`;
    case PropertyType.apartment:
      return `${PROPERTY_TYPE_PREFIX}apartment`;
    case PropertyType.commercial_space:
      return `${PROPERTY_TYPE_PREFIX}commercial_space`;
    case PropertyType.gastronomy:
      return `${PROPERTY_TYPE_PREFIX}gastronomy`;
    case PropertyType.group:
    case 'development':
      return `${PROPERTY_TYPE_PREFIX}group`;
    case PropertyType.hotel:
      return `${PROPERTY_TYPE_PREFIX}hotel`;
    case PropertyType.house:
      return `${PROPERTY_TYPE_PREFIX}house`;
    case PropertyType.industry:
      return `${PROPERTY_TYPE_PREFIX}industry`;
    case PropertyType.investment:
      return `${PROPERTY_TYPE_PREFIX}investment`;
    case PropertyType.land:
      return `${PROPERTY_TYPE_PREFIX}land`;
    case PropertyType.office:
      return `${PROPERTY_TYPE_PREFIX}office`;
    case PropertyType.other:
      return `${PROPERTY_TYPE_PREFIX}other`;
    case PropertyType.store:
      return `${PROPERTY_TYPE_PREFIX}store`;
    default:
      return '';
  }
};

type PropertySubTypeTranslationKey = Extract<SearchModuleTranslationKey, `search.searchbar.advFilter.propertySubType.${string}`>;

export const getPropertySubTypeKey = (subType: PropertySubType): PropertySubTypeTranslationKey | '' => {
  switch (subType) {
    case PropertySubType.apartment_house:
      return `${PROPERTY_SUBTYPE_PREFIX}apartment_house`;
    case PropertySubType.attic:
      return `${PROPERTY_SUBTYPE_PREFIX}attic`;
    case PropertySubType.bungalow:
      return `${PROPERTY_SUBTYPE_PREFIX}bungalow`;
    case PropertySubType.castle:
      return `${PROPERTY_SUBTYPE_PREFIX}castle`;
    case PropertySubType.country_house:
      return `${PROPERTY_SUBTYPE_PREFIX}country_house`;
    case PropertySubType.detached_house:
      return `${PROPERTY_SUBTYPE_PREFIX}detached_house`;
    case PropertySubType.finca:
      return `${PROPERTY_SUBTYPE_PREFIX}finca`;
    case PropertySubType.flat:
      return `${PROPERTY_SUBTYPE_PREFIX}flat`;
    case PropertySubType.investment:
      return `${PROPERTY_SUBTYPE_PREFIX}investment`;
    case PropertySubType.land:
      return `${PROPERTY_SUBTYPE_PREFIX}land`;
    case PropertySubType.loft:
      return `${PROPERTY_SUBTYPE_PREFIX}loft`;
    case PropertySubType.maisonette:
      return `${PROPERTY_SUBTYPE_PREFIX}maisonette`;
    case PropertySubType.other:
      return `${PROPERTY_SUBTYPE_PREFIX}other`;
    case PropertySubType.parking:
      return `${PROPERTY_SUBTYPE_PREFIX}parking`;
    case PropertySubType.penthouse:
      return `${PROPERTY_SUBTYPE_PREFIX}penthouse`;
    case PropertySubType.projects:
      return `${PROPERTY_SUBTYPE_PREFIX}projects`;
    case PropertySubType.semi_detached_house:
      return `${PROPERTY_SUBTYPE_PREFIX}semi_detached_house`;
    case PropertySubType.site:
      return `${PROPERTY_SUBTYPE_PREFIX}site`;
    case PropertySubType.special_property:
      return `${PROPERTY_SUBTYPE_PREFIX}special_property`;
    case PropertySubType.terraced_house:
      return `${PROPERTY_SUBTYPE_PREFIX}terraced_house`;
    case PropertySubType.townhouse:
      return `${PROPERTY_SUBTYPE_PREFIX}townhouse`;
    case PropertySubType.villa:
      return `${PROPERTY_SUBTYPE_PREFIX}villa`;
    default:
      return '';
  }
};

type LifestyleCategoryTranslationKey = Extract<SearchModuleTranslationKey, `search.searchbar.advFilter.lifestyleCategory.${string}`>;

export const getLifestyleCategoryTranslationKey = (category: LifestyleCategory): LifestyleCategoryTranslationKey | undefined => {
  switch (category) {
    case LifestyleCategory.CASTLE_MANORHOUSES:
      return 'search.searchbar.advFilter.lifestyleCategory.castle_manorhouses';
    case LifestyleCategory.EQUESTRIAN:
      return 'search.searchbar.advFilter.lifestyleCategory.equestrian';
    case LifestyleCategory.GOLF:
      return 'search.searchbar.advFilter.lifestyleCategory.golf';
    case LifestyleCategory.MOUNTAIN:
      return 'search.searchbar.advFilter.lifestyleCategory.mountain';
    case LifestyleCategory.PENTHOUSE:
      return 'search.searchbar.advFilter.lifestyleCategory.penthouse';
    case LifestyleCategory.RANCH:
      return 'search.searchbar.advFilter.lifestyleCategory.ranch';
    case LifestyleCategory.SKI:
      return 'search.searchbar.advFilter.lifestyleCategory.ski';
    case LifestyleCategory.VINEYARDS_WINERIES:
      return 'search.searchbar.advFilter.lifestyleCategory.vineyards_wineries';
    case LifestyleCategory.WATERFRONT:
      return 'search.searchbar.advFilter.lifestyleCategory.waterfront';
    default:
      return undefined;
  }
};

type FeatureTranslationKey =
  | 'search.searchbar.advFilter.multimedia.hasFloorplan'
  | 'search.searchbar.advFilter.multimedia.hasVtour'
  | 'search.searchbar.advFilter.multimedia.hasVideo'
  | 'search.searchbar.advFilter.features.hasElevator'
  | 'search.searchbar.advFilter.features.hasTerrace'
  | 'search.searchbar.advFilter.features.hasWaterfront'
  | 'search.searchbar.advFilter.features.hasTennisCourt'
  | 'search.searchbar.advFilter.features.hasAirConditioning'
  | 'search.searchbar.advFilter.features.hasSwimmingPool'
  | 'search.searchbar.advFilter.features.hasSeaOrLakeView'
  | 'search.searchbar.advFilter.features.hasSecuritySystem'
  | 'search.searchbar.advFilter.features.hasBalcony'
  | 'search.searchbar.advFilter.features.hasBasement'
  | 'search.searchbar.advFilter.features.hasPatio'
  | 'search.searchbar.advFilter.features.hasBuiltInKitchen'
  | 'search.searchbar.advFilter.features.hasGarage'
  | 'search.searchbar.advFilter.features.hasGarden';

export function getFeatureKey(feature: string): FeatureTranslationKey | undefined {
  switch (feature) {
    case 'hasFloorplan':
      return 'search.searchbar.advFilter.multimedia.hasFloorplan';
    case 'hasVtourNormalized':
      return 'search.searchbar.advFilter.multimedia.hasVtour';
    case 'hasVideoNormalized':
      return 'search.searchbar.advFilter.multimedia.hasVideo';
    case FeaturesFilter.hasElevator:
      return 'search.searchbar.advFilter.features.hasElevator';
    case FeaturesFilter.hasTerrace:
      return 'search.searchbar.advFilter.features.hasTerrace';
    case FeaturesFilter.hasWaterfront:
      return 'search.searchbar.advFilter.features.hasWaterfront';
    case FeaturesFilter.hasTennisCourt:
      return 'search.searchbar.advFilter.features.hasTennisCourt';
    case FeaturesFilter.hasAirConditioning:
      return 'search.searchbar.advFilter.features.hasAirConditioning';
    case FeaturesFilter.hasSwimmingPool:
      return 'search.searchbar.advFilter.features.hasSwimmingPool';
    case FeaturesFilter.hasSeaOrLakeView:
      return 'search.searchbar.advFilter.features.hasSeaOrLakeView';
    case FeaturesFilter.hasSecuritySystem:
      return 'search.searchbar.advFilter.features.hasSecuritySystem';
    case FeaturesFilter.hasBalcony:
      return 'search.searchbar.advFilter.features.hasBalcony';
    case FeaturesFilter.hasBasement:
      return 'search.searchbar.advFilter.features.hasBasement';
    case FeaturesFilter.hasPatio:
      return 'search.searchbar.advFilter.features.hasPatio';
    case FeaturesFilter.hasBuiltInKitchen:
      return 'search.searchbar.advFilter.features.hasBuiltInKitchen';
    case FeaturesFilter.hasGarage:
      return 'search.searchbar.advFilter.features.hasGarage';
    case FeaturesFilter.hasGarden:
      return 'search.searchbar.advFilter.features.hasGarden';
    default:
      return undefined;
  }
}

type HeatingTypeTranslationKey = Extract<SearchModuleTranslationKey, `search.listingData.energy.heatingType.${string}`>;

export function getHeatingTypeTranslationKey(heatingType: HeatingType): HeatingTypeTranslationKey | undefined {
  switch (heatingType) {
    case HeatingType.heatingTypeunderFloor:
      return 'search.listingData.energy.heatingType.underFloor';
    case HeatingType.heatingTyperadiator:
      return 'search.listingData.energy.heatingType.radiator';
    default:
      return undefined;
  }
}

type HeatingMaterialTranslationKey = Extract<SearchModuleTranslationKey, `search.listingData.energy.heatingMaterial.${string}`>;

const HEATING_MATERIAL_TRANSLATION_KEYS = {
  Loop: 'search.listingData.energy.heatingMaterial.Loop',
  Storm: 'search.listingData.energy.heatingMaterial.Storm',
  aipWp: 'search.listingData.energy.heatingMaterial.aipWp',
  alternative: 'search.listingData.energy.heatingMaterial.alternative',
  bioenergy: 'search.listingData.energy.heatingMaterial.bioenergy',
  block: 'search.listingData.energy.heatingMaterial.block',
  central: 'search.listingData.energy.heatingMaterial.central',
  coal: 'search.listingData.energy.heatingMaterial.coal',
  combineBio: 'search.listingData.energy.heatingMaterial.combineBio',
  combineFossil: 'search.listingData.energy.heatingMaterial.combineFossil',
  combineRegenerative: 'search.listingData.energy.heatingMaterial.combineRegenerative',
  combineRenewable: 'search.listingData.energy.heatingMaterial.combineRenewable',
  deCentral: 'search.listingData.energy.heatingMaterial.deCentral',
  districtHeating: 'search.listingData.energy.heatingMaterial.districtHeating',
  districtHeatingSteam: 'search.listingData.energy.heatingMaterial.districtHeatingSteam',
  electric: 'search.listingData.energy.heatingMaterial.electric',
  environmental: 'search.listingData.energy.heatingMaterial.environmental',
  gas: 'search.listingData.energy.heatingMaterial.gas',
  gasHeavy: 'search.listingData.energy.heatingMaterial.gasHeavy',
  gasHigh: 'search.listingData.energy.heatingMaterial.gasHigh',
  gasLight: 'search.listingData.energy.heatingMaterial.gasLight',
  gasLow: 'search.listingData.energy.heatingMaterial.gasLow',
  heatSupply: 'search.listingData.energy.heatingMaterial.heatSupply',
  liquidGas: 'search.listingData.energy.heatingMaterial.liquidGas',
  localHeating: 'search.listingData.energy.heatingMaterial.localHeating',
  longDistance: 'search.listingData.energy.heatingMaterial.longDistance',
  offPeakStorage: 'search.listingData.energy.heatingMaterial.offPeakStorage',
  oil: 'search.listingData.energy.heatingMaterial.oil',
  pellet: 'search.listingData.energy.heatingMaterial.pellet',
  pump: 'search.listingData.energy.heatingMaterial.pump',
  selfContainedCentral: 'search.listingData.energy.heatingMaterial.selfContainedCentral',
  solar: 'search.listingData.energy.heatingMaterial.solar',
  stove: 'search.listingData.energy.heatingMaterial.stove',
  underFloor: 'search.listingData.energy.heatingMaterial.underFloor',
  waterElectric: 'search.listingData.energy.heatingMaterial.waterElectric',
  waterEnergy: 'search.listingData.energy.heatingMaterial.waterEnergy',
  windEnergy: 'search.listingData.energy.heatingMaterial.windEnergy',
  wood: 'search.listingData.energy.heatingMaterial.wood',
} as const satisfies Record<string, HeatingMaterialTranslationKey>;

export function getHeatingMaterialTranslationKey(heatingMaterial: string): HeatingMaterialTranslationKey | undefined {
  const material = heatingMaterial.replace('heatingMaterial.', '');
  return HEATING_MATERIAL_TRANSLATION_KEYS[material as keyof typeof HEATING_MATERIAL_TRANSLATION_KEYS];
}
